import React, { forwardRef } from 'react';
import classNames from 'classnames';
import Spinner from '../Spinner';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'solid' | 'default' | 'plain' | 'danger' | 'success' | 'warning';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  block?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconAlignment?: 'start' | 'end';
  shape?: 'round' | 'circle';
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      variant = 'default',
      size = 'md',
      block = false,
      loading = false,
      disabled = false,
      icon,
      iconAlignment = 'start',
      shape = 'round',
      type = 'button',
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    const sizeClasses = {
      xs: 'ecme-btn-xs',
      sm: 'ecme-btn-sm',
      md: 'ecme-btn-md',
      lg: 'ecme-btn-lg',
    };

    const variantClasses = {
      solid: 'ecme-btn-solid',
      default: 'ecme-btn-default',
      plain: 'ecme-btn-plain',
      danger: 'ecme-btn-danger',
      success: 'ecme-btn-success',
      warning: 'ecme-btn-warning',
    };

    const classes = classNames(
      'ecme-btn',
      sizeClasses[size],
      variantClasses[variant],
      {
        'ecme-btn-block': block,
        'ecme-btn-disabled': isDisabled,
        'ecme-btn-circle': shape === 'circle',
      },
      className
    );

    const renderContent = () => {
      if (loading) {
        return (
          <span className="flex items-center gap-2">
            <Spinner size="sm" color={variant === 'solid' ? 'white' : 'primary'} />
            {children && <span>{children}</span>}
          </span>
        );
      }

      if (icon && children) {
        return (
          <span className="flex items-center gap-2">
            {iconAlignment === 'start' && icon}
            <span>{children}</span>
            {iconAlignment === 'end' && icon}
          </span>
        );
      }

      if (icon && !children) {
        return icon;
      }

      return children;
    };

    return (
      <button
        ref={ref}
        type={type}
        className={classes}
        disabled={isDisabled}
        {...rest}
      >
        {renderContent()}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
